import { FormEvent, useState, useRef } from 'react';
import classNames from 'classnames';
import { Calendar, CalendarProps } from 'primereact/calendar';
import { parseISO, isValid, parse } from 'date-fns';
import { CalendarIcon } from '@ds-icons';
import { DateFormat, DateFormatMapping, DatePickerChangeVariant, IDatePicker } from './DatePicker.types';
import styles from './DatePicker.module.scss';

export const DatePicker: React.FC<IDatePicker> = (props) => {
  const {
    value,
    label,
    required,
    disabled,
    onChange,
    className,
    placeholder,
    minDate,
    maxDate,
    hasChanged = false,
    highlightOnChange = true,
    panelPosition,
  } = props;
  // ref for positioning calendar panel
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isHavingFormatError, setIsHavingFormatError] = useState(false);
  const calendarDate = value ? parseISO(value) : null;
  const dateFormat = props.dateFormat || DateFormat.MM_DD_YYYY;
  const dateNewFormat = DateFormatMapping[dateFormat as DateFormat];
  const focusedDateFormat = dateNewFormat?.focus;
  const calendarDateFormat = isFocused ? focusedDateFormat : dateNewFormat?.display;
  const datePickerChangedStyle = props.datePickerChangedStyle || DatePickerChangeVariant.Default;

  const isValidDate = (dateString: string, dateFormat: string) => {
    const parsedDate = parse(dateString, dateFormat, new Date());
    return isValid(parsedDate);
  };

  const handleChangeDate = (event: NonNullable<CalendarProps['onChange']> extends (e: infer E) => void ? E : never) => {
    const value = event.value as Date | null;
    onChange && onChange(value);
    setIsHavingFormatError(false);
  };

  const handleBlurCalendar = () => {
    if (!calendarDate && inputValue && !isValidDate(inputValue, focusedDateFormat)) {
      setIsHavingFormatError(true);
    } else {
      setIsHavingFormatError(false);
    }
    setInputValue('');
    setIsFocused(false);
  };

  const handleFocusCalendar = () => {
    setIsFocused(true);
  };

  const handleChangeInput = (event: FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setInputValue(value);
  };

  return (
    <div
      ref={wrapperRef}
      data-testid="ds-datepicker-container"
      className={classNames(styles['ds-datepicker-container'], {
        [styles['ds-disabled-datepicker-container']]: disabled,
      })}
    >
      {label && (
        <label>
          {label} {required && <span className="required-asterisk">*</span>}
        </label>
      )}
      <Calendar
        data-testid="ds-datepicker"
        appendTo="self"
        onShow={() => {
          if (wrapperRef.current && panelPosition) {
            const panel = wrapperRef.current.querySelector('.p-datepicker') as HTMLElement;
            if (!panel) return;
            const height = wrapperRef.current.clientHeight;
            if (panelPosition === 'below') {
              panel.style.top = `${height}px`;
              panel.style.bottom = 'auto';
            } else if (panelPosition === 'above') {
              panel.style.bottom = `${height}px`;
              panel.style.top = 'auto';
            }
          }
        }}
        value={calendarDate}
        onChange={handleChangeDate}
        onBlur={handleBlurCalendar}
        onFocus={handleFocusCalendar}
        onInput={handleChangeInput}
        className={classNames(className, {
          'input-changed': highlightOnChange && hasChanged,
          [`${datePickerChangedStyle}`]: highlightOnChange && hasChanged,
          'error-input': isHavingFormatError,
        })}
        panelClassName={styles['ds-datepicker-panel']}
        placeholder={placeholder || dateFormat.toLowerCase()}
        disabled={disabled}
        dateFormat={calendarDateFormat}
        minDate={minDate}
        maxDate={maxDate}
        showIcon
        showButtonBar
        icon={<img className={styles['calendar-icon']} src={CalendarIcon} alt="Calendar Icon" />}
      />
      {isHavingFormatError && <div className={styles['error-msg']}>Please use {dateFormat} format</div>}
    </div>
  );
};
